import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Rating,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Link,
  Avatar,
  TextField,
  Alert,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { formatDateTimeToLocal } from "../../utils/dateUtils";
import { formatRubles } from "../../utils/currency";
import OrderMap from "../Map/OrderMap";
import ImagePreviewModal from "./ImagePreviewModal";
import CloseOrderModal from "./CloseOrderModal";
import { OrderStatusIndicators } from "./OrderStatusIndicators";
import {
  OrderStatusProgress,
  getStatusTimeout,
  getStatusStartTime,
} from "./OrderStatusProgress";
import { CancellationReasonIcon } from "./CancellationReasonIcon";
import OrderResponsesModal from "./OrderResponsesModal";
import NotificationService from "../../services/WebsocketNotificationService";
import { useNotification } from "../../contexts/NotificationContext";
import api from "../../services/ApiService";

interface OrderDetailModalProps {
  orderId?: number; // ID заказа для загрузки откликов
  open: boolean;
  onClose: () => void;
  order: {
    id: number;
    address: string;
    addressDetails?: string; // Additional address details like apartment, entrance, etc.
    status: string;
    date: string;
    amount: number;
    commission?: number;
    description: string;
    shortDescription?: string; // Short description to display separately
    category:
      | string
      | {
          id: number;
          created_at: string;
          updated_at: string;
          deleted_at?: string;
          name: string;
          description: string;
          localizations?: Array<{
            id: number;
            category_id: number;
            language: string;
            name: string;
            description: string;
          }>;
        };
    rating?: number | null;
    review?: string | null;
    client?: {
      id: number;
      name: string;
      email: string;
    };
    performer?: {
      id: number;
      name: string;
      email: string;
      phone_number?: string;
      average_rating?: number;
      completed_orders_count?: number;
      total_reviews?: number;
    } | null;
    latitude?: number;
    longitude?: number;
    photos?: string[];
    // Expiration-related fields
    expiration_time?: string;
    original_validity_minutes?: number;
    is_expired?: boolean;
    renewed_at?: string;
    remaining_time_seconds?: number;
    // Financial status fields
    funds_blocked?: boolean;
    funds_distributed?: boolean;
    block_transaction_id?: string;
    distribute_transaction_id?: string;
    blocked_at?: string;
    distributed_at?: string;
    // Status timeout tracking fields
    created_at?: string;
    assigned_at?: string;
    work_started_at?: string;
    work_completed_at?: string;
    auto_confirmed_at?: string;
    status_timeout_applied?: boolean;
    // Cancellation fields
    cancellation_reason?: string;
    cancellation_commission_rate?: number;
    cancellation_commission_amount?: number;
    closed_by_customer?: boolean;
    responses_count?: number; // Количество откликов исполнителей
  };
  userId?: number; // ID текущего пользователя
  onOrderCancel?: (orderId: number, reason: string) => void; // Callback для отмены заказа
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  open,
  onClose,
  order,
  userId,
  onOrderCancel,
}) => {
  const { t, i18n } = useTranslation();
  const notification = useNotification(); // Глобальные уведомления
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false); // Диалог отмены заказа
  const [showResponsesModal, setShowResponsesModal] = useState(false); // Модалка откликов
  const [showPerformerProfile, setShowPerformerProfile] = useState(false); // Профиль исполнителя
  const [localOrder, setLocalOrder] = useState(order); // Local order state for real-time updates
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmRating, setConfirmRating] = useState(5);
  const [confirmReview, setConfirmReview] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);

  // Use localOrder instead of order for display
  const displayOrder = localOrder;

  // Проверяем, может ли пользователь подтвердить заказ
  const canConfirmOrder =
    userId &&
    userId === displayOrder.client?.id &&
    displayOrder.status === "completed";

  // Subscribe to WebSocket updates when modal is open
  useEffect(() => {
    if (!open) return;

    // Set initial order
    setLocalOrder(order);

    // Subscribe to order updates
    const unsubscribe = NotificationService.subscribeToOrderUpdates((data) => {
      // Handle performer response
      if (data.type === "performer_responded") {
        console.log("[OrderDetailModal] Performer responded:", data);
        const orderId = data.order_id || data.id;
        if (orderId === displayOrder.id) {
          notification.showInfo(
            `Исполнитель откликнулся на заказ #${orderId}`,
            6000,
          );
          // Refresh order data to update responses_count
          setLocalOrder((prev) => ({
            ...prev,
            responses_count: (prev.responses_count || 0) + 1,
          }));
        }
        return;
      }

      // Handle status change
      const orderId = data.order_id || data.id;
      if (orderId === displayOrder.id) {
        console.log("[OrderDetailModal] Order status changed:", data);
        setLocalOrder((prev) => ({ ...prev, ...data }));

        // Show notification about status change
        const getStatusLabel = (status: string) => {
          return t(`status_${status}`, status);
        };

        const newStatus = data.status ? getStatusLabel(data.status) : "";
        notification.showInfo(
          `Статус заказа #${orderId} изменен на: ${newStatus}`,
          6000,
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, displayOrder.id, notification]);

  // Check if user can cancel the order (any non-final status)
  const cancellableStatuses = [
    "pending",
    "waiting_approval",
    "assigned",
    "in_progress",
    "completed",
    "confirmed",
  ];
  const canCancelOrder =
    userId &&
    userId === displayOrder.client?.id &&
    cancellableStatuses.includes(displayOrder.status);

  // Validation of date before formatting
  const isValidDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const handleOpenImagePreview = (index: number) => {
    setSelectedImageIndex(index);
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
  };

  const handleUploadPhoto = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files || event.target.files.length === 0) return;

    setUploadPhotoLoading(true);

    try {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("photo", file);

      const response = await api.uploadOrderPhoto(displayOrder.id, formData);

      if (response.error) {
        notification.showError(response.error, 4000);
        setUploadPhotoLoading(false);
        event.target.value = "";
        return;
      }

      // Обновляем фото в локальном заказе
      if (response.data?.photo_url) {
        setLocalOrder((prev) => ({
          ...prev,
          photos: [...(prev.photos || []), response.data.photo_url],
        }));
      }

      notification.showSuccess(t("photoUploaded"), 3000);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error || err?.message || t("error");
      notification.showError(errorMessage, 4000);
    } finally {
      setUploadPhotoLoading(false);
      event.target.value = "";
    }
  };

  const handleConfirmOrder = async () => {
    try {
      setConfirmLoading(true);
      await api.confirmOrder(displayOrder.id, confirmRating, confirmReview);
      // Обновляем статус заказа локально и добавляем рейтинг/отзыв
      setLocalOrder((prev) => ({
        ...prev,
        status: "confirmed",
        rating: confirmRating,
        review: confirmReview || null,
      }));
      notification.showSuccess(t("order_confirmed"), 4000);
      setShowConfirmDialog(false);
      setConfirmRating(5);
      setConfirmReview("");
    } catch (err: any) {
      notification.showError(
        err.response?.data?.error || t("order_confirm_error"),
        4000,
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("order_details")} #{displayOrder.id}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("order_info")}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <PlaceIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <strong>{t("address")}:</strong> {displayOrder.address}
                  </Typography>
                </Box>

                {displayOrder.addressDetails && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PlaceIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <strong>{t("address_details")}:</strong>{" "}
                      {displayOrder.addressDetails}
                    </Typography>
                  </Box>
                )}

                {/* Extract address details from description if they exist */}
                {(() => {
                  if (
                    displayOrder.description &&
                    displayOrder.address &&
                    displayOrder.description.includes(displayOrder.address)
                  ) {
                    // Extract any additional address details that might be in the description
                    const addressEndIndex =
                      displayOrder.description.indexOf(displayOrder.address) +
                      displayOrder.address.length;
                    if (addressEndIndex < displayOrder.description.length) {
                      const additionalDetails = displayOrder.description
                        .substring(addressEndIndex)
                        .trim();
                      if (additionalDetails) {
                        // Try to identify if the additional text contains address details
                        // Common patterns for address details: floor numbers, entrance numbers, etc.
                        const addressDetailPattern =
                          /\b(\d+\s*(этаж|подъезд|этаже|etaj|podyezd)|[а-яёa-z]+\s*дом\b)/i;
                        if (addressDetailPattern.test(additionalDetails)) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <PlaceIcon
                                fontSize="small"
                                color="action"
                                sx={{ mr: 1 }}
                              />
                              <Typography variant="body1">
                                <strong>{t("address_details")}:</strong>{" "}
                                {additionalDetails}
                              </Typography>
                            </Box>
                          );
                        }
                      }
                    }
                  }
                  return null;
                })()}

                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AccessTimeIcon
                    fontSize="small"
                    color="action"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body1">
                    <strong>{t("date")}:</strong>{" "}
                    {isValidDate(order.date)
                      ? formatDateTimeToLocal(order.date, i18n.language)
                      : t("invalid_date")}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AttachMoneyIcon
                    fontSize="small"
                    color="action"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body1">
                    <strong>{t("amount")}:</strong>
                    {displayOrder.amount !== undefined &&
                    displayOrder.amount !== null &&
                    displayOrder.amount > 0
                      ? formatRubles(displayOrder.amount)
                      : order.commission !== undefined &&
                          order.commission !== null &&
                          order.commission > 0
                        ? formatRubles(order.commission)
                        : t("friendly_na_message")}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    label={t(`status_${displayOrder.status}`)}
                    color={
                      displayOrder.status === "completed" ||
                      displayOrder.status === "archived"
                        ? "success"
                        : displayOrder.status === "in_progress"
                          ? "warning"
                          : displayOrder.status === "pending"
                            ? "info"
                            : displayOrder.status === "assigned"
                              ? "primary"
                              : displayOrder.status === "cancelled"
                                ? "error"
                                : "default"
                    }
                    size="medium"
                  />

                  {/* Иконка причины отмены */}
                  {displayOrder.status === "cancelled" && (
                    <CancellationReasonIcon
                      reason={displayOrder.cancellation_reason}
                      status={displayOrder.status}
                      cancellationCommissionRate={
                        order.cancellation_commission_rate
                      }
                    />
                  )}

                  {/* Индикаторы блокировки и таймаута */}
                  <OrderStatusIndicators
                    status={displayOrder.status}
                    fundsBlocked={displayOrder.funds_blocked || false}
                    fundsDistributed={displayOrder.funds_distributed || false}
                    assignedAt={displayOrder.assigned_at}
                    workStartedAt={displayOrder.work_started_at}
                    workCompletedAt={displayOrder.work_completed_at}
                    blockedAt={order.blocked_at}
                    distributedAt={order.distributed_at}
                  />

                  {/* Кнопка просмотра откликов для клиента */}
                  {userId &&
                    userId === displayOrder.client?.id &&
                    (displayOrder.status === "pending" ||
                      displayOrder.status === "waiting_approval") &&
                    (displayOrder.responses_count || 0) > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowResponsesModal(true)}
                        startIcon={<Chip label="🙋" size="small" />}
                      >
                        Отклики исполнителей ({displayOrder.responses_count})
                      </Button>
                    )}
                </Box>

                {/* Прогресс-бар таймаута для всех статусов, но с разной активностью */}
                <Box sx={{ mt: 2 }}>
                  <OrderStatusProgress
                    status={displayOrder.status}
                    startTime={getStatusStartTime(displayOrder)}
                    timeoutMinutes={getStatusTimeout(displayOrder.status) || 60}
                    showLabel={true}
                  />
                </Box>

                {/* Expiration information */}
                {(displayOrder.is_expired !== undefined ||
                  order.expiration_time) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1,
                      backgroundColor: displayOrder.is_expired
                        ? "#ffebee"
                        : displayOrder.remaining_time_seconds! < 3600
                          ? "#fff3e0"
                          : "#e8f5e8",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <AccessTimeIcon
                        fontSize="small"
                        color={
                          displayOrder.is_expired
                            ? "error"
                            : displayOrder.remaining_time_seconds! < 3600
                              ? "warning"
                              : "success"
                        }
                        sx={{ mr: 1 }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={
                          displayOrder.is_expired
                            ? "error"
                            : displayOrder.remaining_time_seconds! < 3600
                              ? "warning"
                              : "success"
                        }
                      >
                        {displayOrder.is_expired
                          ? t("order_expired")
                          : displayOrder.remaining_time_seconds !== undefined &&
                              displayOrder.remaining_time_seconds > 0
                            ? `${t("expires_in")} ${Math.floor(displayOrder.remaining_time_seconds / 3600)}:${Math.floor(
                                (displayOrder.remaining_time_seconds % 3600) /
                                  60,
                              )
                                .toString()
                                .padStart(
                                  2,
                                  "0",
                                )}:${(displayOrder.remaining_time_seconds % 60).toString().padStart(2, "0")}`
                            : t("expires_soon")}
                      </Typography>
                    </Box>
                    {displayOrder.original_validity_minutes && (
                      <Typography variant="body2" color="textSecondary">
                        {`${t("validity_period")}: ${displayOrder.original_validity_minutes} ${t("minutes")}`}
                      </Typography>
                    )}
                    {displayOrder.renewed_at && (
                      <Typography variant="body2" color="textSecondary">
                        {`${t("renewed_at")}: ${isValidDate(displayOrder.renewed_at) ? formatDateTimeToLocal(displayOrder.renewed_at, i18n.language) : t("invalid_date")}`}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("additional_info")}
                </Typography>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>{t("category")}:</strong>{" "}
                  {typeof displayOrder.category === "string"
                    ? displayOrder.category
                    : displayOrder.category &&
                        typeof displayOrder.category === "object"
                      ? displayOrder.category.name // Since the backend now returns the correct localized name, we just use it
                      : t("other")}
                </Typography>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>{t("client")}:</strong>{" "}
                  {displayOrder.client?.name || t("not_specified")}
                </Typography>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>{t("performer")}:</strong>{" "}
                  {displayOrder.performer?.name ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setShowPerformerProfile(true)}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "primary.main",
                        "&:hover": { color: "primary.dark" },
                      }}
                    >
                      {displayOrder.performer.name}
                    </Link>
                  ) : (
                    t("not_assigned")
                  )}
                </Typography>

                {displayOrder.rating && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>{t("rating")}:</strong>
                    </Typography>
                    <Rating value={displayOrder.rating} readOnly size="small" />
                  </Box>
                )}

                {displayOrder.review && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>{t("review")}:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {displayOrder.review}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                {/* Only show the main description text, attempting to filter out title and address details */}
                <Typography variant="body1" paragraph>
                  {(() => {
                    let mainDescription = displayOrder.description;

                    // Remove short description from the full description if present
                    if (
                      order.shortDescription &&
                      mainDescription.includes(order.shortDescription)
                    ) {
                      mainDescription = mainDescription
                        .replace(order.shortDescription, "")
                        .trim();
                    }

                    // Remove common prefixes like "заказ №XX" if present
                    mainDescription = mainDescription
                      .replace(/\(заказ №\d+\)\s*/i, "")
                      .trim();

                    // Remove address details that might be appended to the description
                    // This is heuristic - we'll look for common patterns
                    if (
                      displayOrder.address &&
                      mainDescription.includes(displayOrder.address)
                    ) {
                      mainDescription = mainDescription
                        .replace(displayOrder.address, "")
                        .trim();
                    }

                    // More comprehensive approach to extract address details
                    // We'll define a comprehensive list of patterns that indicate address details
                    const addressDetailPatterns = [
                      /\b(\d+\s*(этаж|этаже|etaj|floor))\b/gi,
                      /\b(\d+\s*(подъезд|podyezd|entrance))\b/gi,
                      /\b(\d+\s*(квартира|apt|appartamento|daire|кв\.?))\b/gi,
                      /\b(этаж\s*\d+)\b/gi,
                      /\b(подъезд\s*\d+)\b/gi,
                      /\b(кв\.?\s*\d+)\b/gi,
                      /\b(\d+[\s\-]*этаж)\b/gi,
                      /\b(\d+[\s\-]*подъезд)\b/gi,
                      /\b(\d+[\s\-]*кв\.?)\b/gi,
                    ];

                    // Apply all patterns to remove address details
                    for (const pattern of addressDetailPatterns) {
                      // Reset lastIndex for global regex
                      pattern.lastIndex = 0;
                      mainDescription = mainDescription
                        .replace(pattern, "")
                        .trim();
                    }

                    // Remove address if it appears in the description
                    if (
                      displayOrder.address &&
                      mainDescription.includes(displayOrder.address)
                    ) {
                      mainDescription = mainDescription
                        .replace(displayOrder.address, "")
                        .trim();
                    }

                    // Additional cleanup: remove any remaining address-like fragments
                    // Look for patterns like "5 этаж" anywhere in the description
                    mainDescription = mainDescription
                      .replace(
                        /\s*\d+\s*(этаж|этаже|etaj|floor|подъезд|podyezd|entrance|кв\.?|квартира|apt)\s*/gi,
                        " ",
                      )
                      .trim();

                    // Clean up multiple spaces that might result from removals
                    mainDescription = mainDescription
                      .replace(/\s+/g, " ")
                      .trim();

                    // Clean up extra spaces and return
                    return mainDescription || displayOrder.description; // fallback to original if cleaned result is empty
                  })()}
                </Typography>

                {/* Display photo previews if available */}
                {displayOrder.photos && displayOrder.photos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("photos")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {displayOrder.photos.map((photo, index) => (
                        <img
                          key={index}
                          src={photo}
                          alt={`${t("photo")} ${index + 1}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            cursor: "pointer",
                            border: "2px solid transparent",
                          }}
                          onClick={() => handleOpenImagePreview(index)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#1976d2";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "transparent";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            {displayOrder.latitude && displayOrder.longitude ? (
              <Card>
                <CardContent>
                  <OrderMap
                    latitude={displayOrder.latitude}
                    longitude={displayOrder.longitude}
                    address={displayOrder.address}
                    title={t("location_on_map")}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("location_on_map")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("location_not_available")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {/* Кнопка отмены заказа - показывается только клиенту для статуса pending */}
        {canCancelOrder && (
          <Tooltip
            title={t(
              "free_cancellation_info",
              "Отмена бесплатная в течение 10 минут",
            )}
          >
            <Button
              onClick={() => setShowCancelModal(true)}
              color="error"
              variant="outlined"
              startIcon={<CancelIcon />}
              sx={{ mr: "auto" }}
            >
              {t("cancel_order")}
            </Button>
          </Tooltip>
        )}

        {/* Кнопка подтверждения заказа - показывается только клиенту для статуса completed */}
        {canConfirmOrder && (
          <Button
            onClick={() => setShowConfirmDialog(true)}
            color="success"
            variant="contained"
            startIcon={<CheckCircleIcon />}
          >
            {t("confirm_order")}
          </Button>
        )}

        {/* Кнопка добавления фото - для клиента и исполнителя */}
        {userId &&
          (userId === displayOrder.client?.id ||
            userId === displayOrder.performer?.id) && (
            <Button
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              disabled={uploadPhotoLoading}
            >
              {uploadPhotoLoading ? "Загрузка..." : "Добавить фото"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleUploadPhoto}
              />
            </Button>
          )}

        <Button onClick={onClose} color="inherit">
          {t("close")}
        </Button>
      </DialogActions>

      {/* Диалог подтверждения заказа с оценкой */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => !confirmLoading && setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="success" />
          {t("confirm_order_title")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              {t("confirm_order_message")}
            </Typography>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("your_rating")}
              </Typography>
              <Rating
                value={confirmRating}
                onChange={(_, newValue) => setConfirmRating(newValue || 5)}
                size="large"
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarIcon fontSize="inherit" color="disabled" />}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {confirmRating} из 5
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("your_review")}
              value={confirmReview}
              onChange={(e) => setConfirmReview(e.target.value)}
              variant="outlined"
              disabled={confirmLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            color="inherit"
            disabled={confirmLoading}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmOrder}
            color="success"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            disabled={confirmLoading}
          >
            {confirmLoading
              ? t("confirming")
              : t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно отмены заказа */}
      {showCancelModal && (
        <CloseOrderModal
          open={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          orderId={displayOrder.id}
          orderAmount={displayOrder.amount || order.commission || 0}
          commissionRate={0.1} // 10% комиссия по умолчанию
          onCloseOrder={async (reason: string) => {
            if (onOrderCancel) {
              await onOrderCancel(displayOrder.id, reason);
            }
          }}
        />
      )}

      <ImagePreviewModal
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        images={displayOrder.photos || []}
        initialIndex={selectedImageIndex}
      />

      {/* Order Responses Modal - показывается только если есть orderId */}
      {displayOrder.id && (
        <OrderResponsesModal
          open={showResponsesModal}
          onClose={() => setShowResponsesModal(false)}
          orderId={displayOrder.id}
          onPerformerSelected={(performerId) => {
            // После выбора исполнителя закрываем модальное окно
            setShowResponsesModal(false);
            // Можно добавить callback для обновления заказа
          }}
          onPerformerRejected={(performerId, reason) => {
            // После отклонения исполнителя
            setShowResponsesModal(false);
          }}
          isClient={userId === displayOrder.client?.id}
          showContacts={
            displayOrder.funds_blocked ||
            displayOrder.status === "completed" ||
            displayOrder.status === "archived"
          }
        />
      )}

      {/* Performer Profile Modal */}
      <Dialog
        open={showPerformerProfile}
        onClose={() => setShowPerformerProfile(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "primary.main",
                fontSize: 24,
              }}
            >
              {displayOrder.performer?.name?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6">
                {displayOrder.performer?.name || t("not_specified")}
              </Typography>
              {displayOrder.performer?.average_rating != null &&
                displayOrder.performer.average_rating > 0 && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Rating
                      value={displayOrder.performer.average_rating}
                      readOnly
                      size="small"
                      precision={0.5}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {displayOrder.performer.average_rating.toFixed(1)}
                    </Typography>
                  </Box>
                )}
            </Box>
            <IconButton onClick={() => setShowPerformerProfile(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {displayOrder.performer && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {(() => {
                const showContacts =
                  displayOrder.funds_blocked ||
                  displayOrder.status === "completed" ||
                  displayOrder.status === "archived";
                return showContacts ? (
                  <>
                    {displayOrder.performer.email && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Email:</Typography>
                        <Typography variant="body2">{displayOrder.performer.email}</Typography>
                      </Box>
                    )}
                    {displayOrder.performer.phone_number && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{t("phone")}:</Typography>
                        <Typography variant="body2">{displayOrder.performer.phone_number}</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Alert severity="info" icon={<VisibilityIcon />} sx={{ fontSize: 12 }}>
                    <Typography variant="caption">
                      {t("contacts_hidden_info")}
                    </Typography>
                  </Alert>
                );
              })()}
              <Box display="flex" gap={3}>
                {displayOrder.performer.completed_orders_count != null && (
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary">
                      {displayOrder.performer.completed_orders_count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("completed_orders")}
                    </Typography>
                  </Box>
                )}
                {displayOrder.performer.total_reviews != null && (
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary">
                      {displayOrder.performer.total_reviews}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("total_reviews")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPerformerProfile(false)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OrderDetailModal;
